/**
 * Vault store - Manages saved items and albums.
 */
import { create } from 'zustand';
import { api } from '../api/client';
import { unlockVaultWithPasskey } from '../api/passkey';
import { thumbnailLoader } from '../services/thumbnailLoader';
import type { SavedItem, Album, VaultOverview } from '../types';

// Date group for gallery view
interface DateGroup {
  date: string;
  items: SavedItem[];
}

interface GroupedItemsResponse {
  groups: DateGroup[];
  has_more: boolean;
  oldest_date: string | null;
  oldest_id: number | null;
}

interface VaultState {
  // Data
  albums: Album[];
  overview: VaultOverview | null;

  // Date-grouped data for gallery view
  dateGroups: DateGroup[];
  groupedHasMore: boolean;
  oldestDate: string | null;
  oldestId: number | null;
  /** Which album dateGroups currently shows (null = All) — lets mutations
   * know whether a moved item should leave the visible grid */
  groupedAlbumId: number | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // My Eyes Only
  isPinVerified: boolean;
  privateItems: SavedItem[];
  privateHasMore: boolean;
  privateCursor: number | null;

  // Lockout state
  lockedUntil: Date | null;
  failedAttempts: number;
  remainingAttempts: number;

  // Actions
  fetchOverview: () => Promise<void>;
  fetchGroupedItems: (albumId?: number, reset?: boolean) => Promise<void>;
  fetchPrivateItems: (reset?: boolean) => Promise<void>;
  deleteItem: (itemId: number) => Promise<void>;
  moveItems: (itemIds: number[], albumId: number | null) => Promise<void>;

  // Albums
  createAlbum: (name: string, isPrivate?: boolean) => Promise<Album>;
  updateAlbum: (albumId: number, name?: string, isPrivate?: boolean) => Promise<Album>;
  deleteAlbum: (albumId: number) => Promise<void>;

  // Only Me lock (BUG-044) — unlock is server-side and session-scoped
  unlock: (pin: string) => Promise<boolean>;
  unlockWithPasskey: () => Promise<boolean>;
  lock: () => Promise<void>;
  setPin: (pin: string, currentPin?: string) => Promise<void>;
  clearPinSession: () => void;

  // Settings

  // Clear
  clearVault: () => void;
}

interface ItemsResponse {
  items: SavedItem[];
  has_more: boolean;
  cursor: number | null;
}

export const useVaultStore = create<VaultState>((set, get) => ({
  albums: [],
  overview: null,
  dateGroups: [],
  groupedHasMore: true,
  oldestDate: null,
  oldestId: null,
  groupedAlbumId: null,
  isLoading: false,
  isSaving: false,
  error: null,
  isPinVerified: false,
  privateItems: [],
  privateHasMore: true,
  privateCursor: null,
  lockedUntil: null,
  failedAttempts: 0,
  remainingAttempts: 15,

  fetchOverview: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<VaultOverview>('/vault');
      if (response.success && response.data) {
        set({
          overview: response.data,
          albums: response.data.albums,
          isLoading: false,
        });
      } else {
        set({ error: 'Failed to fetch vault', isLoading: false });
      }
    } catch (error) {
      console.error('Failed to fetch vault overview:', error);
      set({ error: 'Failed to fetch vault', isLoading: false });
    }
  },

  fetchGroupedItems: async (albumId?: number, reset = false) => {
    const { oldestDate, oldestId, dateGroups, groupedHasMore } = get();

    if (!reset && !groupedHasMore) return;

    set({ isLoading: true, error: null });
    try {
      const params: Record<string, string | number> = { limit: 100 };
      if (albumId !== undefined) params.album_id = albumId;
      if (!reset && oldestDate) {
        // Compound cursor: before_id lets the server resume INSIDE a day the
        // previous page cut mid-way (BUG-047 #4)
        params.before_date = oldestDate;
        if (oldestId) params.before_id = oldestId;
      }

      const response = await api.get<GroupedItemsResponse>(
        '/vault/items/grouped',
        { params }
      );

      if (response.success && response.data) {
        const newGroups = response.data.groups;

        // Merge groups - if loading more, append to existing groups
        let mergedGroups: DateGroup[];
        if (reset) {
          mergedGroups = newGroups;
        } else {
          // Check if last existing date matches first new date (merge them)
          const existingGroups = [...dateGroups];
          if (existingGroups.length > 0 && newGroups.length > 0) {
            const lastExisting = existingGroups[existingGroups.length - 1];
            const firstNew = newGroups[0];
            if (lastExisting.date === firstNew.date) {
              // Merge items for same date
              lastExisting.items = [...lastExisting.items, ...firstNew.items];
              mergedGroups = [...existingGroups, ...newGroups.slice(1)];
            } else {
              mergedGroups = [...existingGroups, ...newGroups];
            }
          } else {
            mergedGroups = [...existingGroups, ...newGroups];
          }
        }

        set({
          dateGroups: mergedGroups,
          groupedHasMore: response.data.has_more,
          oldestDate: response.data.oldest_date,
          oldestId: response.data.oldest_id,
          groupedAlbumId: albumId ?? null,
          isLoading: false,
        });
      } else {
        set({ error: 'Failed to fetch items', isLoading: false });
      }
    } catch (error) {
      console.error('Failed to fetch grouped vault items:', error);
      set({ error: 'Failed to fetch items', isLoading: false });
    }
  },

  fetchPrivateItems: async (reset = false) => {
    const { privateCursor, privateItems, privateHasMore } = get();

    if (!reset && !privateHasMore) return;

    set({ isLoading: true, error: null });
    try {
      const params: Record<string, string | number> = { limit: 50 };
      if (!reset && privateCursor) params.before_id = privateCursor;

      // BUG-044: the code used to ride along in an X-Vault-Pin header the
      // browser never actually sent (not in the CORS allow-list). Access is
      // now a server-side unlock held against the session — see unlock().
      const response = await api.get<ItemsResponse>('/vault/items/private', {
        params,
      });

      if (response.success && response.data) {
        const newItems = response.data.items;
        set({
          privateItems: reset ? newItems : [...privateItems, ...newItems],
          privateHasMore: response.data.has_more,
          privateCursor: response.data.cursor,
          isPinVerified: true,
          isLoading: false,
        });
      } else {
        // Only a real lock from the server re-locks the tab. A network blip
        // must NOT silently bounce the user back to the code pad (BUG-047).
        const code = (response.error as { code?: string } | undefined)?.code;
        const isLocked = code === 'VAULT_LOCKED' || code === 'UNAUTHORIZED';
        set({
          error: isLocked ? null : "Couldn't load your Only Me items",
          isLoading: false,
          ...(isLocked ? { isPinVerified: false } : {}),
        });
      }
    } catch (error) {
      console.error('Failed to fetch private items:', error);
      set({ error: "Couldn't load your Only Me items", isLoading: false });
    }
  },

  deleteItem: async (itemId: number) => {
    // OPTIMISTIC: Update UI immediately, API runs in background
    set((state) => ({
      privateItems: state.privateItems.filter((i) => i.id !== itemId),
      // Also update dateGroups (used by VaultPage gallery grid)
      dateGroups: state.dateGroups
        .map(group => ({
          ...group,
          items: group.items.filter((i) => i.id !== itemId),
        }))
        .filter(group => group.items.length > 0), // Remove empty groups
      overview: state.overview ? {
        ...state.overview,
        total_items: Math.max(0, state.overview.total_items - 1),
      } : null,
    }));

    // API call in background (don't await for UI)
    try {
      await api.delete(`/vault/items/${itemId}`);
    } catch (error) {
      console.error('Failed to delete item from server:', error);
      // Item already removed from UI - could add back on error, but keeping simple
    }
  },

  moveItems: async (itemIds: number[], albumId: number | null) => {
    try {
      await api.post('/vault/items/move', {
        item_ids: itemIds,
        album_id: albumId,
      });

      // Update the RENDERED state — the old code touched a list no screen
      // read, so moving appeared to do nothing until a reload (BUG-047 #5).
      set((state) => {
        const moved = new Set(itemIds);

        // Album item_count follows the items in both directions
        const countDelta = new Map<number, number>();
        for (const group of state.dateGroups) {
          for (const item of group.items) {
            if (moved.has(item.id) && item.album_id !== albumId) {
              if (item.album_id != null) {
                countDelta.set(
                  item.album_id,
                  (countDelta.get(item.album_id) ?? 0) - 1
                );
              }
              if (albumId != null) {
                countDelta.set(albumId, (countDelta.get(albumId) ?? 0) + 1);
              }
            }
          }
        }

        const inAlbumView = state.groupedAlbumId !== null;
        const dateGroups = state.dateGroups
          .map((group) => ({
            ...group,
            items: group.items
              // Items moved OUT of the album being viewed leave the grid
              .filter(
                (item) =>
                  !(
                    inAlbumView &&
                    moved.has(item.id) &&
                    albumId !== state.groupedAlbumId
                  )
              )
              .map((item) =>
                moved.has(item.id) ? { ...item, album_id: albumId } : item
              ),
          }))
          .filter((group) => group.items.length > 0);

        return {
          dateGroups,
          privateItems: state.privateItems.map((item) =>
            moved.has(item.id) ? { ...item, album_id: albumId } : item
          ),
          albums: state.albums.map((album) =>
            countDelta.has(album.id)
              ? {
                  ...album,
                  item_count:
                    (album.item_count ?? 0) + (countDelta.get(album.id) ?? 0),
                }
              : album
          ),
        };
      });
    } catch (error) {
      console.error('Failed to move items:', error);
      throw error;
    }
  },

  createAlbum: async (name: string, isPrivate = false) => {
    try {
      const response = await api.post<Album>(
        '/vault/albums',
        { name, is_private: isPrivate }
      );

      if (response.success && response.data) {
        const album = response.data;
        set((state) => ({
          albums: [album, ...state.albums],
          overview: state.overview ? {
            ...state.overview,
            albums: [album, ...state.overview.albums],
          } : null,
        }));
        return album;
      }

      throw new Error('Failed to create album');
    } catch (error) {
      console.error('Failed to create album:', error);
      throw error;
    }
  },

  updateAlbum: async (albumId: number, name?: string, isPrivate?: boolean) => {
    try {
      const response = await api.patch<Album>(
        `/vault/albums/${albumId}`,
        { name, is_private: isPrivate }
      );

      if (response.success && response.data) {
        const updated = response.data;
        set((state) => ({
          albums: state.albums.map((a) => (a.id === albumId ? updated : a)),
        }));
        return updated;
      }

      throw new Error('Failed to update album');
    } catch (error) {
      console.error('Failed to update album:', error);
      throw error;
    }
  },

  deleteAlbum: async (albumId: number) => {
    try {
      await api.delete(`/vault/albums/${albumId}`);

      set((state) => ({
        albums: state.albums.filter((a) => a.id !== albumId),
        // Items in this album become unorganized
        dateGroups: state.dateGroups.map((group) => ({
          ...group,
          items: group.items.map((item) =>
            item.album_id === albumId ? { ...item, album_id: null } : item
          ),
        })),
      }));
    } catch (error) {
      console.error('Failed to delete album:', error);
      throw error;
    }
  },

  // BUG-044: unlock the private space for this session. The server holds the
  // unlocked state for 5 minutes; nothing about the code is kept here.
  unlock: async (pin: string) => {
    const response = await api.post<{ unlocked: boolean }>('/vault/unlock', { pin });

    if (response.success && response.data?.unlocked) {
      set({ isPinVerified: true, lockedUntil: null, failedAttempts: 0 });
      return true;
    }

    // api.* never throws — a rejection arrives as {success:false, error}.
    // The old code read err.response.status here, which never existed, so
    // the lockout countdown could never appear.
    const detail = response.error as
      | { code?: string; message?: string; remaining_attempts?: number; retry_after?: number }
      | undefined;

    if (detail?.code === 'VAULT_LOCKED' || detail?.code === 'RATE_LIMITED') {
      set({
        isPinVerified: false,
        // The server deliberately doesn't say for how long, so show the
        // shortest tier rather than inventing a number.
        lockedUntil: new Date(Date.now() + 60_000),
      });
      return false;
    }

    set({
      isPinVerified: false,
      remainingAttempts: detail?.remaining_attempts ?? get().remainingAttempts,
    });
    return false;
  },

  // Face ID / fingerprint / device passcode — and the forgot-my-code path.
  // A passkey proves the account owner, so it works even during a lockout.
  unlockWithPasskey: async () => {
    const result = await unlockVaultWithPasskey();
    if (result.success) {
      set({ isPinVerified: true, lockedUntil: null, failedAttempts: 0 });
      return true;
    }
    return false;
  },

  lock: async () => {
    // Decrypted private thumbnails must not outlive the lock — revoke just
    // those (clearAll would blank the public grid too; the cache is shared)
    get().privateItems.forEach((item) => thumbnailLoader.invalidate(item.id));
    set({
      isPinVerified: false,
      privateItems: [],
      privateHasMore: true,
      privateCursor: null,
    });
    await api.post('/vault/lock', {});
  },

  setPin: async (pin: string, currentPin?: string) => {
    try {
      await api.post('/vault/pin', currentPin ? { pin, current_pin: currentPin } : { pin });

      // OPTIMISTIC UPDATE - immediately set both isPinVerified AND has_pin
      // This prevents the "Create PIN" UI from reappearing while we wait for server refresh
      set((state) => ({
        isPinVerified: true,
        overview: state.overview ? {
          ...state.overview,
          settings: {
            ...state.overview.settings,
            has_pin: true,
          },
        } : null,
      }));

      // Still refresh overview from server to ensure consistency
      const response = await api.get<VaultOverview>('/vault');
      if (response.success && response.data) {
        set({
          overview: response.data,
          albums: response.data.albums,
        });
      }
    } catch (error) {
      console.error('Failed to set PIN:', error);
      throw error;
    }
  },

  clearPinSession: () => {
    get().privateItems.forEach((item) => thumbnailLoader.invalidate(item.id));
    set({
      isPinVerified: false,
      privateItems: [],
      privateHasMore: true,
      privateCursor: null,
    });
  },

  clearVault: () => {
    set({
      albums: [],
      overview: null,
      dateGroups: [],
      groupedHasMore: true,
      oldestDate: null,
      oldestId: null,
      groupedAlbumId: null,
      isLoading: false,
      isSaving: false,
      error: null,
      isPinVerified: false,
      privateItems: [],
      privateHasMore: true,
      privateCursor: null,
      lockedUntil: null,
      failedAttempts: 0,
      remainingAttempts: 15,
    });
  },
}));
